import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { getClinroUrl } from 'utils/studyConfigUtils'
import STRINGS from 'utils/strings'
import '../stylesheets/ClinroIframe.scss'

const ClinroIframe = ({ userToken, studyId, siteId }) => {
    const baseUrl = getClinroUrl()
    const iframeRef = useRef(null)
    const [didHandshake, setDidHandshake] = useState(false)

    // Do NOT pass auth tokens via query params (they leak via logs/history/referrers).
    // We only include non-sensitive context in the URL.
    const clinroUrl = useMemo(() => {
        const queryParams = new URLSearchParams({
            studyId: studyId?.toString() || '',
            siteId: siteId?.toString() || '',
            isAdminPanel: 'true',
        })
        return `${baseUrl}/login?${queryParams.toString()}`
    }, [baseUrl, studyId, siteId])

    const clinroOrigin = useMemo(() => {
        try {
            return new URL(baseUrl).origin
        } catch (e) {
            return '*'
        }
    }, [baseUrl])

    const sendAuthMessage = useCallback(() => {
        if (!userToken) return
        const win = iframeRef.current?.contentWindow
        if (!win) return

        win.postMessage(
            {
                type: 'CLINRO_ADMINPANEL_AUTH',
                token: userToken,
                studyId: studyId?.toString() || '',
                siteId: siteId?.toString() || '',
                isAdminPanel: true,
            },
            clinroOrigin === '*' ? '*' : clinroOrigin,
        )
    }, [userToken, studyId, siteId, clinroOrigin])

    useEffect(() => {
        if (!userToken) return

        const onMessage = event => {
            // Only accept messages from the ClinRO app origin.
            if (clinroOrigin !== '*' && event.origin !== clinroOrigin) return

            const data = event.data || {}
            // ClinRO can signal it's ready to receive the auth payload (avoids timing flakiness).
            if (data?.type === 'CLINRO_ADMINPANEL_AUTH_READY') {
                sendAuthMessage()
                return
            }
            if (data?.type === 'CLINRO_ADMINPANEL_AUTH_ACK') {
                setDidHandshake(true)
            }
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [clinroOrigin, userToken, sendAuthMessage])

    // On iframe load, push token via postMessage. Retry a few times in case the app
    // hasn't registered its message listener yet.
    useEffect(() => {
        if (!userToken || didHandshake) return
        let attempts = 0
        const maxAttempts = 10

        const interval = setInterval(() => {
            attempts += 1
            sendAuthMessage()
            if (attempts >= maxAttempts) clearInterval(interval)
        }, 300)

        return () => clearInterval(interval)
    }, [userToken, didHandshake, sendAuthMessage])

    return (
        <div className='clinro-iframe page'>
            <div className='clinro-header'>
                <h4>{STRINGS.clinro}</h4>
            </div>
            <iframe
                className='clinro-embedded-iframe'
                src={clinroUrl}
                id='clinro-iframe'
                title='ClinRO Application'
                allowFullScreen
                ref={iframeRef}
                onLoad={() => {
                    setDidHandshake(false)
                    sendAuthMessage()
                }}
                // ClinRO may use document.referrer as a fallback for origin checks when no allowlist is configured.
                // Using "origin" avoids leaking path/query while still enabling that fallback.
                referrerPolicy='origin'
            />
        </div>
    )
}

ClinroIframe.propTypes = {
    userToken: PropTypes.string.isRequired,
    studyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ClinroIframe

